import { BUILT_IN_PROFILES } from 'trace-viewer/media-utils';
import type { SizeEstimate, Verdict, VideoProbeReport } from 'trace-viewer/media-utils';
import type { UiState } from './main';
import { changeFields, formatBitrate, formatDuration, summarizeChanges } from './changes';
import {
  axesToFixes,
  escapeHtml,
  formatFileSize,
  renderChangeRow,
  renderChecks,
  renderDisclosure,
  renderDropZone,
  renderFacts,
  renderFileChip,
  renderFixSection,
  renderKv,
  renderPageHeader,
  renderProgress,
  renderQualityPicker,
  renderSizeMeter,
  renderStatusHead,
  type QualityOption,
} from '../shared/ui';

interface RenderProps {
  state: UiState;
  verdict: Verdict | null;
  /** The verdict is for the optimized output, not the original. */
  judgingOutput: boolean;
  command: string;
  estimate: SizeEstimate | null;
}

const ACCEPT = 'video/*,.mp4,.mov,.m4v,.webm,.mkv';

/** The shared quality words, mapped onto the video profiles. High → Small. */
const QUALITY: Record<string, Omit<QualityOption, 'id'>> = {
  'AR-mobile-hq': { label: 'High', hint: 'Sharper and larger. Best when viewers are on Wi-Fi.' },
  'AR-mobile': { label: 'Balanced', hint: 'Recommended. Good quality that still loads quickly on mobile data.' },
  'AR-lite': { label: 'Small', hint: 'Smallest file, for slow connections or background loops.' },
};

const QUALITY_OPTIONS: QualityOption[] = ['AR-mobile-hq', 'AR-mobile', 'AR-lite']
  .filter(id => BUILT_IN_PROFILES.some(p => p.id === id))
  .map(id => ({ id, ...QUALITY[id] }));

export function qualityOf(id: string): Omit<QualityOption, 'id'> {
  return QUALITY[id] ?? { label: 'Custom', hint: '' };
}

/** Plain-language names for each check. */
const CHECK_LABELS: Record<string, string> = {
  container: 'File format',
  faststart: 'Starts playing quickly',
  codec: 'Video codec',
  codecFourcc: 'Codec tag',
  profile: 'Codec profile',
  level: 'Codec level',
  resolution: 'Resolution',
  bitrate: 'Bitrate',
  framerate: 'Frame rate',
  audio: 'Audio',
  pixelAspect: 'Pixel shape',
  evenDimensions: 'Dimensions',
  chromaSubsampling: 'Color sampling',
  bitDepth: 'Bit depth',
  colorRange: 'Color range',
  duration: 'Length',
  fileSize: 'File size',
};

const HEADLINE: Record<Verdict['overall'], string> = {
  green: 'Ready for AR',
  yellow: 'Plays in AR, but could be lighter',
  red: 'Needs optimizing for AR',
};

function keyFacts(report: VideoProbeReport): Array<[string, string]> {
  const v = report.video;
  const facts: Array<[string, string]> = [];
  if (v) facts.push(['Resolution', `${v.width} × ${v.height}`]);
  if (report.durationSec > 0) facts.push(['Length', formatDuration(report.durationSec)]);
  if (v?.framerate != null) facts.push(['Frame rate', `${Math.round(v.framerate)} fps${v.framerateMode === 'vfr' ? ' (variable)' : ''}`]);
  if (v) facts.push(['Codec', v.codec]);
  facts.push(['Audio', report.audio ? report.audio.codec : 'None']);
  return facts;
}

// ─── Result panel ──────────────────────────────────────────────────────────

function renderResult(state: UiState, verdict: Verdict | null, judgingOutput: boolean, estimate: SizeEstimate | null): string {
  const report = state.report ?? state.sniffReport;
  if (!report || !state.file) return '';
  // Facts follow the verdict: once it judges the optimized file, show its numbers.
  const shown = judgingOutput && state.optimize.outputReport ? state.optimize.outputReport : report;

  const quality = qualityOf(state.selectedProfile.id);
  const head = verdict
    ? renderStatusHead(verdict.overall, HEADLINE[verdict.overall],
        `${judgingOutput ? 'Checked after optimizing' : 'Checked on the original file'}, against the <strong>${escapeHtml(quality.label)}</strong> target.`)
    : renderStatusHead('busy', 'Checking the video…', 'Quick scan done. Reading the whole file.');

  return `
    <div class="status-line">${head}</div>
    ${renderFacts(keyFacts(shown))}
    ${renderQualityPicker(QUALITY_OPTIONS, state.selectedProfile.id, state.optimize.running)}
    ${state.report ? renderFixAction(state, verdict, estimate) : ''}
  `;
}

/** The "fix" block: optimize button + estimate, progress, or the finished file. */
function renderFixAction(state: UiState, verdict: Verdict | null, estimate: SizeEstimate | null): string {
  const o = state.optimize;
  const file = state.file!;
  const error = o.error ? `<div class="notice error"><strong>Optimizing failed.</strong> ${escapeHtml(o.error)} Try the ffmpeg command under Details.</div>` : '';

  if (o.running) {
    const label = o.stage === 'loading' ? 'Starting optimizer' : o.stage === 'transcoding' ? 'Optimizing video' : 'Finishing up';
    return renderProgress(label, o.ratio);
  }

  const current = o.output && o.outputProfileId === state.selectedProfile.id;
  if (current && o.output) {
    const summary = state.report && o.outputReport ? summarizeChanges(state.report, o.outputReport) : '';
    return `
      ${renderSizeMeter(file.size, o.output.size, {
        note: `Optimized at <strong>${escapeHtml(qualityOf(o.outputProfileId!).label)}</strong> quality.${summary ? ` ${escapeHtml(summary)}` : ''}`,
      })}
      <div class="status-actions">
        <button class="btn-primary btn-large" data-action="download-output">Download ${escapeHtml(o.outputName ?? 'video')}</button>
      </div>
    `;
  }

  if (state.support && !state.support.supported) {
    return `
      <div class="notice">
        <strong>This browser can’t optimize video.</strong>
        ${escapeHtml(state.support.reason ?? '')} Use the ffmpeg command instead — it produces the same result.
      </div>
      <div class="status-actions">
        <button class="btn-primary" data-action="copy-command">Copy ffmpeg command</button>
      </div>
    `;
  }

  const ready = verdict?.overall === 'green';
  const stale = o.output ? ' The last result was made at a different quality.' : '';
  const meter = estimate && estimate.bytes > 0
    ? renderSizeMeter(file.size, estimate.bytes, {
        estimate: true,
        note: `Estimated. ${ready ? 'Already within the target, so this is optional.' : 'Runs in your browser, using hardware encoding where available.'}${stale}`,
      })
    : '';
  return `
    ${meter}
    <div class="status-actions">
      <button class="${ready ? '' : 'btn-primary '}btn-large" data-action="optimize">${ready ? 'Optimize anyway' : 'Optimize video'}</button>
    </div>
    ${error}
  `;
}

// ─── What changed ──────────────────────────────────────────────────────────

function renderChangeLog(before: VideoProbeReport, after: VideoProbeReport): string {
  const b = changeFields(before);
  const a = changeFields(after);
  return Object.keys(a).map(k => renderChangeRow(k, b[k], a[k])).join('');
}

// ─── Details ───────────────────────────────────────────────────────────────

function fileDetailRows(report: VideoProbeReport, phase: UiState['phase']): Array<[string, string]> {
  const v = report.video;
  const a = report.audio;
  const yesNo = (b: boolean): string => (b ? 'Yes' : 'No');
  const rows: Array<[string, string]> = [
    ['Format', `${report.container.format}${report.container.brands.length ? ` (${report.container.brands.join(', ')})` : ''}`],
    ['File size', formatFileSize(report.container.fileSize)],
    ['Starts playing quickly', `${yesNo(report.container.faststart)} (faststart)`],
    ['Fragmented', yesNo(report.container.fragmented)],
  ];
  if (report.durationSec > 0) rows.push(['Length', `${report.durationSec.toFixed(2)} s`]);
  if (v) {
    rows.push(['Video codec', `${v.codec}${v.codecFourcc ? ` (${v.codecFourcc})` : ''}`]);
    rows.push(['Resolution', v.isAnamorphic && v.codedWidth && v.codedHeight
      ? `${v.width} × ${v.height} shown, ${v.codedWidth} × ${v.codedHeight} stored`
      : `${v.width} × ${v.height}`]);
    if (v.sampleAspectRatio) {
      rows.push(['Pixel shape', v.isAnamorphic ? `${v.sampleAspectRatio} (stretched, non-square pixels)` : 'Square']);
    }
    rows.push(['Frame rate', v.framerate != null ? `${v.framerate.toFixed(2)} fps${v.framerateMode === 'vfr' ? ' (variable)' : ''}` : 'Unknown']);
    rows.push(['Video bitrate', formatBitrate(v.bitrateBps)]);
    if (v.profile) rows.push(['H.264 profile', v.profile]);
    if (v.level) rows.push(['H.264 level', v.level]);
    if (v.chromaSubsampling && v.chromaSubsampling !== 'yuv420p' && v.chromaSubsampling !== 'unknown') {
      rows.push(['Color sampling', v.chromaSubsampling]);
    }
    if (v.bitDepth != null && v.bitDepth !== 8) rows.push(['Bit depth', `${v.bitDepth}-bit`]);
    if (v.colorRange === 'pc') rows.push(['Color range', 'Full (pc)']);
    if (v.hdr === true) rows.push(['HDR', 'Yes']);
  } else if (phase === 'done') {
    rows.push(['Video', 'None']);
  }
  if (a) {
    rows.push(['Audio', `${a.codec}, ${a.channels} ch, ${a.sampleRateHz} Hz, ${formatBitrate(a.bitrateBps)}`]);
  } else if (phase === 'done') {
    rows.push(['Audio', 'None']);
  }
  return rows;
}

function renderDetails(state: UiState, verdict: Verdict | null, command: string, estimate: SizeEstimate | null): string {
  const report = state.report;
  if (!report) return '';

  const notes = report.warnings.length
    ? `<p class="setting-help" style="margin-top:1rem;">${report.warnings.map(w => escapeHtml(w.message)).join(' ')}</p>`
    : '';

  const checks = verdict
    ? renderDisclosure('checks', 'All checks', renderChecks(verdict.axes, CHECK_LABELS), {
        meta: `${verdict.axes.filter(a => a.status === 'green').length} of ${verdict.axes.length} pass`,
      })
    : '';

  const estimateLine = estimate && estimate.bytes > 0
    ? `About ${formatFileSize(estimate.bytes)}. ${escapeHtml(estimate.explanation)}`
    : 'No size estimate for this file.';
  const ffmpeg = command
    ? renderDisclosure('ffmpeg', 'Use ffmpeg instead', `
        <div class="command-actions">
          <span class="setting-help">Run in a terminal with <a href="https://ffmpeg.org/download.html" target="_blank" rel="noopener">ffmpeg</a> installed. ${estimateLine}</span>
          <button class="btn-small" data-action="copy-command">Copy command</button>
        </div>
        <pre class="command-block">${escapeHtml(command)}</pre>
      `)
    : '';

  const o = state.optimize;
  const changes = o.outputReport && o.outputProfileId
    ? renderDisclosure('changes', 'What optimizing changed', renderChangeLog(report, o.outputReport), {
        meta: `${qualityOf(o.outputProfileId).label} result`,
      })
    : '';

  return `
    <section class="section">
      <h2 class="section-title">Details</h2>
      ${checks}
      ${changes}
      ${renderDisclosure('file', 'File details', renderKv(fileDetailRows(report, state.phase)) + notes)}
      ${ffmpeg}
    </section>
  `;
}

// ─── Page ──────────────────────────────────────────────────────────────────

export function renderApp(root: HTMLElement, props: RenderProps): void {
  const { state, verdict, judgingOutput, command, estimate } = props;

  const input = state.file
    ? renderFileChip(state.file, ACCEPT)
    : renderDropZone({ accept: ACCEPT, prompt: 'Drop a video here', hint: 'MP4, MOV, M4V or WebM.' });

  const busy = state.phase === 'sniffing'
    ? `<div class="busy-line"><span class="status-light busy"></span>Reading the video…</div>`
    : '';
  const error = state.error
    ? `<div class="notice error" style="margin-top:1rem;"><strong>Couldn’t read this video.</strong> ${escapeHtml(state.error)}</div>`
    : '';

  const lengthFailing = verdict?.axes.some(a => a.axis === 'duration' && a.status !== 'green');
  const fixes = verdict
    ? renderFixSection(
        'What to fix',
        axesToFixes(verdict.axes, CHECK_LABELS),
        judgingOutput ? 'Nothing to fix. The optimized video is within the target.' : 'Nothing to fix. This video is within the target.',
        judgingOutput
          ? `Left after optimizing.${lengthFailing ? ' Length has to be trimmed in your video editor.' : ''}`
          : `Optimize video handles the encoding issues.${lengthFailing ? ' Length has to be trimmed in your video editor.' : ''}`,
      )
    : '';

  ensureShell(root);
  const setSlot = (name: string, html: string): void => {
    const el = root.querySelector(`[data-slot="${name}"]`);
    if (el) el.innerHTML = html;
  };
  setSlot('input', input);
  setSlot('notice', busy + error);
  const result = root.querySelector<HTMLElement>('section.result');
  if (result) result.hidden = !(state.report ?? state.sniffReport);
  setSlot('result', renderResult(state, verdict, judgingOutput, estimate));
  setSlot('fixes', fixes);
  setSlot('details', renderDetails(state, verdict, command, estimate));
}

/**
 * Build the stable page shell ONCE. Re-renders rewrite only the slots; the
 * `#player-slot` is owned by player.ts and never rewritten, so playback isn't
 * reset by the progress updates that re-render the page many times a second.
 */
function ensureShell(root: HTMLElement): void {
  if (root.querySelector('#player-slot')) return;
  root.innerHTML = `
    ${renderPageHeader('video', 'Video optimizer', 'Check whether a video will play smoothly in AR, then make it smaller and more compatible.')}
    <div data-slot="input"></div>
    <div data-slot="notice"></div>
    <section class="result" hidden>
      <div class="result-body" data-slot="result"></div>
      <div id="player-slot" class="video-stage" hidden></div>
    </section>
    <div data-slot="fixes"></div>
    <div data-slot="details"></div>
  `;
}
